// Character names and the chat word filter. The server has the final say on names (and its reserved names and banned words).
import { GameConstants } from '../constants';

export interface ReservedName { name: string; prefix: boolean }
export class ReservedNames {
  readonly names: readonly ReservedName[];
  constructor(names: readonly ReservedName[]) {
    const seen = new Set<string>();
    this.names = names.map(n => ({ ...n, name: n.name.trim().toLowerCase() }))
      .filter(n => /^[a-z]{2,}$/.test(n.name) && !seen.has(n.name) && !!seen.add(n.name))
      .sort((a, b) => a.name < b.name ? -1 : a.name > b.name ? 1 : 0);
  }
  static parse(text: string): ReservedNames {
    return new ReservedNames(text.split('\n').map(s => s.trim()).filter(s => s && !s.startsWith('#'))
      .map(s => ({ name: s.endsWith('*') ? s.slice(0, -1) : s, prefix: s.endsWith('*') })));
  }
  isReserved(name: string): boolean {
    const n = name.trim().toLowerCase();
    return this.names.some(r => r.name === n || (r.prefix && n.startsWith(r.name)));
  }
}

const asciiLetter = /^[A-Za-z]$/;
const asciiLetterOrDigit = /^[A-Za-z0-9]$/;

/** Checks a character name's format. Returns the normalised name ("eMbEr" → "Ember") or an error. */
export function validateCharacterName(raw: string): { name: string | null; error: string | null } {
  const name = raw.trim();
  if (name.length < 3) return { name: null, error: 'Names must be at least 3 letters.' };
  if (name.length > 16) return { name: null, error: 'Names can be at most 16 letters.' };
  for (const c of name) {
    if (c === ' ') return { name: null, error: "Names can't contain spaces." };
    if (!asciiLetter.test(c)) return { name: null, error: 'Names can only use the letters A-Z.' };
  }
  return { name: name[0].toUpperCase() + name.slice(1).toLowerCase(), error: null };
}

/**
 * Matches words in names and text. Letters repeated in the text still match ("fuuuck"), but a word's own letters must
 * all be there. Whole-only words ("=ass" in the list) match only as a whole name or word.
 */
export class WordFilter {
  private readonly anywhere: RegExp | null;
  private readonly wholeName: RegExp | null;
  private readonly wholeWord: RegExp | null;

  constructor(words: { word: string; wholeOnly: boolean }[]) {
    const seen = new Set<string>();
    const valid = words.map(w => ({ word: w.word.trim().toLowerCase(), wholeOnly: w.wholeOnly }))
      .filter(w => w.word.length >= 3 && /^[a-z]+$/.test(w.word) && !seen.has(w.word) && seen.add(w.word))
      .sort((a, b) => (a.word < b.word ? -1 : 1));
    const pattern = (w: string) => [...w].map(c => `${c}+`).join('');
    const any = (whole: boolean) => valid.filter(w => w.wholeOnly === whole).map(w => pattern(w.word)).join('|');
    const a = any(false), whole = any(true);
    this.anywhere = a ? new RegExp(a, 'gi') : null;
    this.wholeName = whole ? new RegExp(`^(?:${whole})$`, 'i') : null;
    this.wholeWord = whole ? new RegExp(`(?<![a-z])(?:${whole})(?![a-z])`, 'gi') : null;
  }

  /** A word list file: one word per line, "=word" for whole-only, '#' comments and blank lines ignored. */
  static parse(text: string): WordFilter {
    const words: { word: string; wholeOnly: boolean }[] = [];
    for (const raw of text.split('\n')) {
      const line = raw.trim();
      if (!line || line.startsWith('#')) continue;
      const whole = line.startsWith('=');
      words.push({ word: whole ? line.slice(1) : line, wholeOnly: whole });
    }
    return new WordFilter(words);
  }

  matchesName(name: string): boolean {
    return (this.anywhere !== null && new RegExp(this.anywhere.source, 'i').test(name)) || (this.wholeName?.test(name) ?? false);
  }

  /** The text with every word replaced by asterisks of the same length. */
  mask(text: string): string {
    const stars = (m: string) => '*'.repeat(m.length);
    if (this.anywhere) text = text.replace(this.anywhere, stars);
    if (this.wholeWord) text = text.replace(this.wholeWord, stars);
    return text;
  }
}
